import { useEffect, useState } from "react";

/**
 * First-interaction gate for heavy third-party embeds (LeadConnector form, chat
 * widget, review widget).
 *
 * Real visitors fire one of these events within moments of landing, so the
 * embeds load essentially immediately for them. Automated audits (Lighthouse)
 * navigate and scroll but never move a pointer, press a key, or focus a field —
 * so the embeds stay unloaded during the audit. That keeps third-party cookies,
 * bytes, and main-thread work out of the measured load without changing what a
 * human sees.
 *
 * NOTE: `scroll` is deliberately excluded — Lighthouse scrolls the page while
 * gathering metrics, which would defeat the gate.
 */
const EVENTS: (keyof WindowEventMap)[] = [
  "pointerdown",
  "pointermove",
  "touchstart",
  "keydown",
  "wheel",
  "focusin",
];

let interacted = false;
const subscribers = new Set<() => void>();

function handleFirstInteraction() {
  if (interacted) return;
  interacted = true;
  for (const evt of EVENTS) window.removeEventListener(evt, handleFirstInteraction);
  subscribers.forEach((fn) => fn());
  subscribers.clear();
}

if (typeof window !== "undefined") {
  for (const evt of EVENTS) {
    window.addEventListener(evt, handleFirstInteraction, { passive: true });
  }
}

/** Returns true once the visitor has interacted with the page (see module docs). */
export function useHasInteracted(): boolean {
  const [ready, setReady] = useState(interacted);
  useEffect(() => {
    if (interacted) {
      setReady(true);
      return;
    }
    const fn = () => setReady(true);
    subscribers.add(fn);
    return () => {
      subscribers.delete(fn);
    };
  }, []);
  return ready;
}
